import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, TrendingUp, CreditCard, Eye, Download, LayoutGrid, BookOpen, AlertTriangle, Stamp, Briefcase } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { getEmployees, createEmployee, updateEmployee, deleteEmployee, calculateEOSB, togglePortalAccess, reinstateEmployee } from '../services/api';
import EmployeeForm from '../components/EmployeeForm';
import PromotionHistorySection from '../components/employee-form/PromotionHistorySection';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import { 
  EmployeeCard, 
  EmployeeFilters, 
  EOSBDialog, 
  AdvancesSection,
  AddSuspensionDialog, 
  ManageSuspensionsDialog 
} from '../components/employees';
import { EOSBPreviewDialog, LeaveSettlementDialog } from '../components/DocumentPreviewDialog';
import WarningsDialog from '../components/employees/WarningsDialog';
import MeetingEmailDialog from '../components/employees/MeetingEmailDialog';
import StaffDirectory from '../components/employees/StaffDirectory';
import VisaCostsTab from '../components/visa-costs/VisaCostsTab';
import HiringCostsTab from '../components/hiring-costs/HiringCostsTab';
import DuplicateScannerDialog from '../components/data-hygiene/DuplicateScannerDialog';
import { PortalBulkButtons } from '../components/employees/PortalBulkButtons';
import { DeptManagersButton } from '../components/employees/DeptManagersButton';
import { PositionCleanupButton } from '../components/employees/PositionCleanupButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [showAllEmployees, setShowAllEmployees] = useState(false);
  const [showDupeScanner, setShowDupeScanner] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active'); // active | inactive | all
  const [view, setView] = useState(() => { const v = new URLSearchParams(window.location.search).get('view'); return ['visa', 'hiring'].includes(v) ? v : 'cards'; }); // 'cards' | 'directory' | 'visa' | 'hiring'
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [promotionDialogOpen, setPromotionDialogOpen] = useState(false);
  const [suspensionDialogOpen, setSuspensionDialogOpen] = useState(false);
  const [suspensionManageDialogOpen, setSuspensionManageDialogOpen] = useState(false);
  const [advancesDialogOpen, setAdvancesDialogOpen] = useState(false);
  const [warningsDialogOpen, setWarningsDialogOpen] = useState(false);
  const [eosbPreviewOpen, setEosbPreviewOpen] = useState(false);
  const [leaveSettlementOpen, setLeaveSettlementOpen] = useState(false);
  const [meetingEmailOpen, setMeetingEmailOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [eosbData, setEosbData] = useState(null);
  
  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [isPermanentDelete, setIsPermanentDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Portal deactivation dialog state
  const [portalDialogOpen, setPortalDialogOpen] = useState(false);
  const [portalEmployee, setPortalEmployee] = useState(null);
  const [portalAction, setPortalAction] = useState(true); // true=activate, false=deactivate
  const [portalReason, setPortalReason] = useState('');
  const [reinstateEmp, setReinstateEmp] = useState(null);
  const [reinstateDate, setReinstateDate] = useState('');
  const [reinstating, setReinstating] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isViewer = user.role === 'viewer';
  const departments = [...new Set(employees.map(emp => emp.department).filter(Boolean))].sort();

  useEffect(() => {
    loadEmployees();
  }, []);

  // Deep link: /employees?focus=<id> shows that employee and opens their profile
  useEffect(() => {
    const focusId = new URLSearchParams(window.location.search).get('focus');
    if (!focusId) return;
    axios.get(`${API_URL}/api/employees/${focusId}`).then((r) => {
      setSearchTerm(r.data.employee_code || r.data.name || '');
      if (!isViewer) { setSelectedEmployee(r.data); setDialogOpen(true); }
    }).catch(() => toast({ title: 'Employee not found', variant: 'destructive' }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEmployees = async () => {
    try {
      // Load ALL employees (active_only=false), frontend will handle filtering
      const response = await getEmployees(false);
      setEmployees(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load employees', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (selectedEmployee) {
        await updateEmployee(selectedEmployee.id, data);
        toast({ title: 'Success', description: 'Employee updated successfully' });
      } else {
        try {
          await createEmployee(data);
        } catch (err) {
          const detail = err?.response?.data?.detail;
          if (err?.response?.status === 409 && detail?.code === 'employee_duplicate') {
            const list = (detail.clashes || []).map(c =>
              `• ${c.employee_code || '—'} · ${c.name} · matched on ${c.matched_on.join(', ')}`
            ).join('\n');
            if (!window.confirm(
              `⚠ Duplicate employee warning\n\n${detail.message}\n\n${list}\n\nCreate this employee anyway?`
            )) {
              return;
            }
            await createEmployee(data, true);
          } else {
            throw err;
          }
        }
        toast({ title: 'Success', description: 'Employee added successfully' });
      }
      loadEmployees();
      setDialogOpen(false);
      setSelectedEmployee(null);
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail?.[0]?.msg || error.response?.data?.detail || 'Failed to save employee', 
        variant: 'destructive' 
      });
    }
  };

  const handleDelete = async (id, permanent = false) => {
    const emp = employees.find(e => e.id === id);
    setEmployeeToDelete(emp);
    setIsPermanentDelete(permanent);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteEmployee = async () => {
    if (!employeeToDelete) return;
    setDeleting(true);
    
    try {
      await deleteEmployee(employeeToDelete.id, isPermanentDelete);
      toast({ 
        title: 'Success', 
        description: isPermanentDelete ? 'Employee permanently deleted' : 'Employee deactivated' 
      });
      loadEmployees();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete employee', variant: 'destructive' });
    } finally {
      setDeleting(false);
      setEmployeeToDelete(null);
    }
  };

  const handleCalculateEOSB = async (employee) => {
    // Open the EOSB preview dialog instead of just calculating
    setSelectedEmployee(employee);
    setEosbPreviewOpen(true);
  };

  const handleAddPromotion = async (promotionData) => {
    if (!selectedEmployee) return;
    try {
      await axios.post(`${API_URL}/api/employees/${selectedEmployee.id}/promotion`, promotionData);
      toast({ title: 'Success', description: 'Promotion added successfully' });
      loadEmployees();
      const response = await axios.get(`${API_URL}/api/employees/${selectedEmployee.id}`);
      setSelectedEmployee(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      console.error('Promotion error:', detail, error);
      toast({ title: 'Error', description: `Failed to add promotion: ${detail}`, variant: 'destructive' });
    }
  };

  const handleDeletePromotion = async (promotionId) => {
    if (!selectedEmployee) return;
    try {
      await axios.delete(`${API_URL}/api/employees/${selectedEmployee.id}/promotion/${promotionId}`);
      toast({ title: 'Success', description: 'Promotion record deleted' });
      loadEmployees();
      const response = await axios.get(`${API_URL}/api/employees/${selectedEmployee.id}`);
      setSelectedEmployee(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      toast({ title: 'Error', description: `Failed to delete promotion: ${detail}`, variant: 'destructive' });
    }
  };

  const handleDownload = async (employeeId, type, format) => {
    try {
      toast({ title: 'Preparing Download', description: `Generating ${type.replace('-', ' ').toUpperCase()} ${format.toUpperCase()} file...` });
      const response = await fetch(`${API_URL}/api/downloads/${type}/${employeeId}?format=${format}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${type}_${employeeId}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1].replace(/"/g, '');
      }
      
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      toast({ title: 'Download Complete', description: `${type.replace('-', ' ').toUpperCase()} ${format.toUpperCase()} downloaded` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download file', variant: 'destructive' });
    }
  };

  const handleDownloadStaffPDF = async (employeeId, employeeName) => {
    try {
      toast({ title: 'Preparing Download', description: `Generating PDF for ${employeeName}...` });
      const response = await fetch(`${API_URL}/api/employees/${employeeId}/pdf`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${employeeName.replace(' ', '_')}_profile.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      toast({ title: 'Download Complete', description: 'Staff profile PDF downloaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download PDF', variant: 'destructive' });
    }
  };

  const openReinstate = (employee) => {
    setReinstateEmp(employee);
    setReinstateDate(new Date().toISOString().slice(0, 10));
  };

  const confirmReinstate = async () => {
    if (!reinstateDate) { toast({ title: 'Pick a reinstatement date', variant: 'destructive' }); return; }
    setReinstating(true);
    try {
      const res = await reinstateEmployee(reinstateEmp.id, reinstateDate);
      const gapDays = res?.data?.total_gap_days ?? 0;
      toast({ title: 'Employee reinstated', description: `${reinstateEmp.name} is Active again. ${gapDays} day(s) excluded from working days & service.` });
      setReinstateEmp(null);
      loadEmployees();
    } catch (error) {
      toast({ title: 'Error', description: error?.response?.data?.detail || 'Failed to reinstate employee', variant: 'destructive' });
    } finally {
      setReinstating(false);
    }
  };

  const handleTogglePortal = async (employee, activate) => {
    if (activate) {
      // Activate directly, no reason needed
      try {
        await togglePortalAccess(employee.id, { active: true, reason: '' });
        toast({ title: 'Success', description: `Portal activated for ${employee.employee_code} (${employee.name})` });
        loadEmployees();
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to activate portal', variant: 'destructive' });
      }
    } else {
      // Open dialog for deactivation reason
      setPortalEmployee(employee);
      setPortalAction(false);
      setPortalReason('');
      setPortalDialogOpen(true);
    }
  };

  const handleConfirmPortalDeactivation = async () => {
    if (!portalReason) {
      toast({ title: 'Required', description: 'Please select a reason', variant: 'destructive' });
      return;
    }
    try {
      await togglePortalAccess(portalEmployee.id, { active: false, reason: portalReason });
      toast({ title: 'Success', description: `Portal deactivated for ${portalEmployee.employee_code} (${portalEmployee.name})` });
      setPortalDialogOpen(false);
      loadEmployees();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to deactivate portal', variant: 'destructive' });
    }
  };

  const handleDownloadActiveStaff = async (format) => {
    try {
      toast({ title: 'Preparing Download', description: `Generating Active Staff ${format.toUpperCase()} report...` });
      const response = await fetch(`${API_URL}/api/employees/download/active-staff?format=${format}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Active_Staff_List.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast({ title: 'Download Complete', description: `Active Staff ${format.toUpperCase()} downloaded` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download report', variant: 'destructive' });
    }
  };


  // Helper function to check if employee is effectively active (including those in notice period)
  const isEffectivelyActive = (emp) => {
    const status = emp.employment_status;

    // If status is Active, they're active
    if (status === 'Active') return true;

    // Hard-inactive statuses — never active (even after any grace period)
    if (['Absconding', 'In Prison', 'Death', 'Visa Cancelled Outside Country'].includes(status)) {
      return false;
    }

    // If Resigned/Terminated, check if last working date hasn't passed
    if (['Resigned', 'Terminated'].includes(status)) {
      const lastDateStr = emp.last_working_date || emp.resignation_date || emp.termination_date;
      if (lastDateStr) {
        try {
          const lastDate = new Date(lastDateStr);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          lastDate.setHours(0, 0, 0, 0);
          return lastDate >= today;
        } catch (e) {
          return false;
        }
      }
      return false;
    }
    
    // Other statuses like Suspended, Probation - they're still active
    return true;
  };

  // Filtering logic
  const activeEmployees = employees.filter(emp => isEffectivelyActive(emp));
  const inactiveEmployees = employees.filter(emp => !isEffectivelyActive(emp));
  const displayedEmployees = (
    statusFilter === 'inactive' ? inactiveEmployees :
    statusFilter === 'all' ? employees :
    activeEmployees
  );
  
  const filteredEmployees = displayedEmployees.filter(emp => {
    const matchesSearch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (emp.employee_code && emp.employee_code.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesDepartment = selectedDepartment === 'all' || emp.department === selectedDepartment;
    return matchesSearch && matchesDepartment;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-3xl font-bold">Employees</h1>
          <p className="text-gray-600 mt-1">Manage your staff database</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 border rounded-lg overflow-hidden" data-testid="download-active-staff">
            <Button variant="outline" size="sm" className="rounded-none border-0 text-xs gap-1.5" onClick={() => handleDownloadActiveStaff('excel')} data-testid="download-staff-excel">
              <Download className="h-3.5 w-3.5" /> Excel
            </Button>
            <div className="w-px h-6 bg-gray-200" />
            <Button variant="outline" size="sm" className="rounded-none border-0 text-xs gap-1.5" onClick={() => handleDownloadActiveStaff('pdf')} data-testid="download-staff-pdf">
              <Download className="h-3.5 w-3.5" /> PDF
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDupeScanner(true)}
            data-testid="scan-employee-dupes-btn"
            className="border-rose-200 text-rose-700 hover:bg-rose-50 gap-1.5"
          >
            <AlertTriangle className="h-3.5 w-3.5" /> Scan duplicates
          </Button>
          {!isViewer && <DeptManagersButton employees={employees} onDone={loadEmployees} />}
          {!isViewer && <PositionCleanupButton onDone={loadEmployees} />}
          {!isViewer && <PortalBulkButtons onDone={loadEmployees} />}
          {!isViewer && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setSelectedEmployee(null); setDialogOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" /> Add Employee
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-screen overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{selectedEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
              </DialogHeader>
              <EmployeeForm employee={selectedEmployee} onSave={handleSave} onCancel={() => setDialogOpen(false)} />
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      <EmployeeFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedDepartment={selectedDepartment}
        setSelectedDepartment={setSelectedDepartment}
        departments={departments}
        showAllEmployees={showAllEmployees}
        setShowAllEmployees={setShowAllEmployees}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        activeCount={activeEmployees.length}
        totalCount={employees.length}
        inactiveCount={inactiveEmployees.length}
        filteredCount={filteredEmployees.length}
      />

      {/* View toggle */}
      <div className="flex items-center gap-2 border-b pb-2" data-testid="employee-view-toggle">
        <button
          onClick={() => setView('cards')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border-b-2 transition ${view === 'cards' ? 'border-violet-500 text-violet-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
          data-testid="view-cards"
        >
          <LayoutGrid className="h-4 w-4" /> Cards View
        </button>
        <button
          onClick={() => setView('directory')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border-b-2 transition ${view === 'directory' ? 'border-violet-500 text-violet-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
          data-testid="view-directory"
        >
          <BookOpen className="h-4 w-4" /> Staff Directory
        </button>
        <button
          onClick={() => setView('visa')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border-b-2 transition ${view === 'visa' ? 'border-violet-500 text-violet-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
          data-testid="view-visa-costs"
        >
          <Stamp className="h-4 w-4" /> Visa Costs & Renewals
        </button>
        <button
          onClick={() => setView('hiring')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border-b-2 transition ${view === 'hiring' ? 'border-violet-500 text-violet-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
          data-testid="view-hiring-costs"
        >
          <Briefcase className="h-4 w-4" /> Hiring Costs
        </button>
      </div>

      {view === 'hiring' ? (
        <HiringCostsTab />
      ) : view === 'visa' ? (
        <VisaCostsTab />
      ) : view === 'directory' ? (
        <StaffDirectory />
      ) : (
      <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEmployees.map((employee) => (
          <EmployeeCard
            key={employee.id}
            employee={employee}
            isViewer={isViewer}
            onEdit={(emp) => { setSelectedEmployee(emp); setDialogOpen(true); }}
            onDelete={handleDelete}
            onPromotion={(emp) => { setSelectedEmployee(emp); setPromotionDialogOpen(true); }}
            onSuspension={(emp) => { setSelectedEmployee(emp); setSuspensionManageDialogOpen(true); }}
            onAdvances={(emp) => { setSelectedEmployee(emp); setAdvancesDialogOpen(true); }}
            onWarnings={(emp) => { setSelectedEmployee(emp); setWarningsDialogOpen(true); }}
            onEOSB={handleCalculateEOSB}
            onLeaveSettlement={(emp) => { setSelectedEmployee(emp); setLeaveSettlementOpen(true); }}
            onMeetingEmail={(emp) => { setSelectedEmployee(emp); setMeetingEmailOpen(true); }}
            onDownloadStaffPDF={handleDownloadStaffPDF}
            onDownload={handleDownload}
            onTogglePortal={handleTogglePortal}
            onReinstate={openReinstate}
            allEmployees={employees}
          />
        ))}
      </div>

      {filteredEmployees.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            No employees found
          </CardContent>
        </Card>
      )}
      </>
      )}

      {/* Reinstate dialog */}
      <Dialog open={!!reinstateEmp} onOpenChange={(o) => !o && setReinstateEmp(null)}>
        <DialogContent className="max-w-sm" data-testid="reinstate-dialog">
          <DialogHeader>
            <DialogTitle>Reinstate {reinstateEmp?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              This makes the employee <strong>Active</strong> again. The gap between their last working day
              and the reinstatement date is <strong>not counted</strong> as working days or length of service.
            </p>
            <div>
              <label className="text-xs text-gray-600">Reinstatement date</label>
              <input
                type="date"
                value={reinstateDate}
                onChange={(e) => setReinstateDate(e.target.value)}
                className="w-full h-10 px-2 border rounded-md text-sm mt-1"
                data-testid="reinstate-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReinstateEmp(null)}>Cancel</Button>
            <Button onClick={confirmReinstate} disabled={reinstating} className="bg-green-600 hover:bg-green-700" data-testid="reinstate-confirm-btn">
              {reinstating ? 'Reinstating…' : 'Reinstate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EOSB Dialog */}
      <EOSBDialog
        eosbData={eosbData}
        employee={selectedEmployee}
        onClose={() => setEosbData(null)}
        onUpdate={loadEmployees}
      />

      {/* EOSB Preview Dialog with Date Selection */}
      <EOSBPreviewDialog
        open={eosbPreviewOpen}
        onClose={() => setEosbPreviewOpen(false)}
        employee={selectedEmployee}
        onEmployeeUpdate={loadEmployees}
      />

      {/* Final Leave Salary Settlement Dialog */}
      <LeaveSettlementDialog
        open={leaveSettlementOpen}
        onClose={() => setLeaveSettlementOpen(false)}
        employee={selectedEmployee}
      />

      <MeetingEmailDialog
        open={meetingEmailOpen}
        onOpenChange={setMeetingEmailOpen}
        employee={selectedEmployee}
      />

      {/* Promotion History Dialog */}
      <Dialog open={promotionDialogOpen} onOpenChange={setPromotionDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Promotion History - {selectedEmployee?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedEmployee && (
            <PromotionHistorySection
              promotionHistory={selectedEmployee.promotion_history || []}
              currentPosition={selectedEmployee.position}
              currentSalary={selectedEmployee.salary}
              employeeId={selectedEmployee.id}
              onAddPromotion={handleAddPromotion}
              onDeletePromotion={handleDeletePromotion}
              onPromotionChanged={async () => {
                loadEmployees();
                const response = await axios.get(`${API_URL}/api/employees/${selectedEmployee.id}`);
                setSelectedEmployee(response.data);
              }}
              isViewer={isViewer}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Suspension Dialogs */}
      <AddSuspensionDialog
        open={suspensionDialogOpen}
        onOpenChange={setSuspensionDialogOpen}
        employee={selectedEmployee}
        onSuccess={loadEmployees}
      />
      
      <ManageSuspensionsDialog
        open={suspensionManageDialogOpen}
        onOpenChange={setSuspensionManageDialogOpen}
        employee={selectedEmployee}
        onSuccess={loadEmployees}
        onAddNew={(emp) => { setSelectedEmployee(emp); setSuspensionDialogOpen(true); }}
      />

      {/* Advances Dialog */}
      <Dialog open={advancesDialogOpen} onOpenChange={setAdvancesDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-blue-600" />
              Salary Advances - {selectedEmployee?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedEmployee && (
            <AdvancesSection
              employee={selectedEmployee}
              onUpdate={loadEmployees}
              isViewer={isViewer}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Employee Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={isPermanentDelete ? "Permanently Delete Employee" : "Deactivate Employee"}
        description={
          isPermanentDelete 
            ? "Are you sure you want to PERMANENTLY DELETE this employee? This action cannot be undone and all associated data (leaves, advances, etc.) will be removed."
            : "Are you sure you want to deactivate this employee? They will no longer appear in active employee lists but their data will be preserved."
        }
        itemName={employeeToDelete?.name}
        onConfirm={confirmDeleteEmployee}
        loading={deleting}
        variant={isPermanentDelete ? "danger" : "warning"}
        confirmText={isPermanentDelete ? "Permanently Delete" : "Deactivate"}
      />

      {/* Warnings Dialog */}
      <WarningsDialog
        open={warningsDialogOpen}
        onOpenChange={setWarningsDialogOpen}
        employee={selectedEmployee}
        onSuccess={loadEmployees}
        isViewer={isViewer}
      />

      {/* Portal Deactivation Dialog */}
      <Dialog open={portalDialogOpen} onOpenChange={setPortalDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Deactivate Portal Access</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">
              Deactivating portal for <span className="font-semibold">{portalEmployee?.employee_code} ({portalEmployee?.name})</span>. They will no longer be able to log into the Employee Portal.
            </p>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Reason for deactivation</label>
              <select
                value={portalReason}
                onChange={(e) => setPortalReason(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid="portal-deactivation-reason"
              >
                <option value="">Select reason...</option>
                <option value="Terminated">Terminated</option>
                <option value="Resigned">Resigned</option>
                <option value="Absconded">Absconded</option>
                <option value="No Longer Working">No Longer Working</option>
                <option value="End of Contract">End of Contract</option>
                <option value="Suspended">Suspended</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setPortalDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" size="sm" onClick={handleConfirmPortalDeactivation} data-testid="confirm-deactivate-portal">
                Deactivate Portal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <DuplicateScannerDialog open={showDupeScanner} onOpenChange={setShowDupeScanner} scope="employees" />
    </div>
  );
};

export default Employees;
